import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';

import { DropDownContainer } from '../SideBar/DropDownContainer';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { FormButtonsContainer } from '@components/Container';
import Drawer from '@components/Drawer';
import { Modal } from '@components/Modal';
import { MANUAL, manualKey } from '@config/manuals';

interface DropDownProps {
  children?: React.ReactNode;
  onClick: () => void;
}

export const DropdownItems: React.FC<DropDownProps> = ({
  children,
  onClick,
}) => {
  return (
    <div className="px-4 md:py-1.5 py-1">
      <button
        className="md:text-sm text-xs font-light text-gray-700 w-full text-start hover:font-medium hover:text-primary-600 transition-colors"
        onClick={onClick}
      >
        {children}
      </button>
    </div>
  );
};

interface Props {
  setUserManual: (value: boolean) => void;
}

export const UserManuals: React.FC<Props> = ({ setUserManual }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeManualKey, setActiveManualKey] = useState<string | null>(null);
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  const toggleCategory = (category: string) => {
    setOpenCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const iframeRef = useRef<HTMLIFrameElement>(null);

  return (
    <Drawer isOpen={true} title="" onClose={() => setUserManual(false)}>
      <div className="flex flex-col h-full w-full bg-white rounded-t-lg md:rounded-lg overflow-hidden">
        <div className="p-4 pb-2 border-b border-gray-100">
          <div className="font-bold md:text-base text-sm px-2 py-1 text-gray-800">
            User Manual
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 custom-scrollbar">
          {MANUAL.map((group) => {
            const filteredItems = group.items.filter(
              (item) =>
                item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                group.category.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (filteredItems.length === 0) return null;

            if (group.category === '') {
              return filteredItems.map((m) => {
                const key = manualKey(m.label);
                return (
                  <div
                    key={key}
                    className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <button
                      className="w-full text-start md:text-sm text-xs font-medium text-gray-700"
                      onClick={() => setActiveManualKey(key)}
                    >
                      {m.label}
                    </button>
                  </div>
                );
              });
            }

            const isOpen =
              openCategories.includes(group.category) || searchTerm.length > 0;

            return (
              <div key={group.category} className="space-y-1">
                <div
                  className="flex items-center justify-between p-3 bg-white border border-gray-100 shadow-sm rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleCategory(group.category)}
                >
                  <div className="md:text-sm text-xs font-medium text-gray-800">
                    {group.category}
                  </div>
                  <ChevronDownIcon
                    className={`md:w-4 w-3 text-gray-500 transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </div>

                {isOpen && (
                  <div className="mt-1 bg-gray-50/50 rounded-xl py-1 border border-gray-50">
                    <DropDownContainer>
                      {filteredItems.map((m) => {
                        const key = manualKey(m.label);
                        return (
                          <DropdownItems
                            key={key}
                            onClick={() => setActiveManualKey(key)}
                          >
                            {m.label}
                          </DropdownItems>
                        );
                      })}
                    </DropDownContainer>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t border-gray-100 bg-white">
          <div className="w-full relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:text-sm text-xs rounded-xl border border-gray-300 p-2.5 pr-9 outline-none transition-all focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white placeholder:text-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {activeManualKey && (
        <Modal show={true}>
          <CloseButton onClick={() => setActiveManualKey(null)} />
          <Box>
            <div className="pb-4 overflow-hidden rounded-t-lg">
              <iframe
                ref={iframeRef}
                src={(() => {
                  let foundItem = null;
                  let foundCategory = '';

                  for (const group of MANUAL) {
                    const item = group.items.find(
                      (i) => manualKey(i.label) === activeManualKey
                    );
                    if (item) {
                      foundItem = item;
                      foundCategory = group.category;
                      break;
                    }
                  }

                  if (!foundItem) return '';

                  const isExternal = foundItem.label.startsWith('http');
                  const pathPrefix = foundCategory ? `${foundCategory}/` : '';
                  const baseUrl = isExternal
                    ? foundItem.label
                    : `/manuals/${pathPrefix}${foundItem.label}.pdf`;

                  return `${baseUrl}#view=FitH&navpanes=0&toolbar=0`;
                })()}
                width="100%"
                height="100%"
                className="block min-h-[60vh] md:min-h-[700px]"
              />
            </div>
            <FormButtonsContainer>
              <button
                onClick={() => setActiveManualKey(null)}
                className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 w-full md:w-auto"
              >
                Close
              </button>
            </FormButtonsContainer>
          </Box>
        </Modal>
      )}
    </Drawer>
  );
};
